import { POLL_MAX_OPTIONS } from "../../polls/types";
import { getSidebarItem } from "../../../config/sidebarConfig";
import { CREATE_NOTICE } from "../../../routes";
import { toDriveUpload } from "../../../utils/driveNavigation";
import { noticesQuery, templatesQuery } from "../../../services/queries";
import { viewQuery } from "../scopedQuery";
import { canOpenSidebarItem } from "../../navigation/sidebarAccess";
import { trimFiller } from "../fillerWords";
import { createPageTemplates, mapApiTemplates } from "../../notices/createPageTemplates";
import type { NoticeRestoreSource } from "../../notices/noticePayload";
import type { WizardHandover } from "../../notices/wizardSession";
import { getFeatureFlagsFromUser, isFeatureFlagEnabled } from "../../../utils/featureFlags";
import { LATEST } from "../taskDetails";
import { toNewNotice, toNewImageNotice } from "../writingRequest";
import { askFor, findByName } from "./findByName";
import { askedHow } from "./askedHow";
import { openPageEntry } from "./openPage";
import type { HandoffEntry, HowToEntry } from "./types";

const NOTHING_SENT = "Nothing is sent until you press Publish.";

const toCreateNotice = (state: WizardHandover) => ({ pathname: CREATE_NOTICE, state });

/**
 * "Send a notice to Grade 2 about Friday's trip": a guided Notice, asking for the
 * title, message and who it goes to, then the wizard on Review. "Create a poll" is a
 * guided Poll, asking for the question, its choices and who it goes to. "Send a
 * reminder" isn't one of these: a Reminder goes out on a Notice already sent, from
 * Notices → Sent, so this never starts a new Notice for it.
 */
const noticeEntry: HandoffEntry = {
  id: "create.notice",
  sidebarItem: "create",
  guided: (said) => (said.noticeType === "reminder" ? null : said.noticeType === "poll" ? "poll" : "notice"),
  examples: [
    "Send a notice about Friday's trip",
    "Send a notice",
    "Send a new notice",
    "Write a notice",
    "Send out a notice",
    "Post a notice",
    "Put out a notice",
    "Create a notice",
    "Send an announcement",
    "Make an announcement",
    "Send a message",
    "Write a message",
    "Send a direct message",
    "Let parents know",
    "Send an urgent notice",
    "Make an urgent notice",
    "Create a poll",
    "Send a poll",
    "Draft a poll",
    "Send a notice to one parent",
    ...askedHow("tell members"),
  ],
};

export const OFFER_TO_SEND_TEXT =
  "Sure I can. I'll help prepare the Notice, but you review it and press Publish yourself. Nothing is sent until then. What kind of notice would you like to send?";

/** A chip, or a short answer to it: "Event", "an urgent notice", "a poll please". */
const BARE_KIND =
  /^(?:an?\s+|the\s+)?(?:urgent\s+)?(?:information|info|notices?|events?|polls?|urgent|announcements?|messages?|regular|plain|normal)(?:\s+notice)?$/i;

const NOTICE_KINDS = [
  { test: /\burgent\b/i, task: "notice" as const, startedBy: "create.notice", bare: "Send an urgent notice" },
  { test: /\bpolls?\b/i, task: "poll" as const, startedBy: "create.notice", bare: "Create a poll" },
  { test: /\bevents?\b/i, task: "event" as const, startedBy: "calendar.createEvent", bare: "Create an event" },
  {
    test: /\b(?:information|info|notices?|announcements?|messages?|regular|plain|normal)\b/i,
    task: "notice" as const,
    startedBy: "create.notice",
    bare: "Send a notice",
  },
];

export type NoticeKindStart = { task: "notice" | "event" | "poll"; startedBy: string; request: string };

/**
 * The kind they named after being asked, and the request that starts it.
 * A bare choice ("Event") starts that kind with nothing filled in. A longer
 * answer ("an event on Friday about sports day") is the request itself, so
 * those details are kept. A question ("What is an Event?") is not a choice.
 */
export function noticeKindStart(text: string): NoticeKindStart | null {
  const said = trimFiller(text.trim().replace(/[\s?.!]+$/, ""));
  if (!said || (!BARE_KIND.test(said) && /^(?:what|who|where|how|why|when|is|are|do|does|can|could|would|will|should)\b/i.test(said))) {
    return null;
  }
  const found = NOTICE_KINDS.find((kind) => kind.test.test(said));
  if (!found) return null;
  return { task: found.task, startedBy: found.startedBy, request: BARE_KIND.test(said) ? found.bare : text.trim() };
}

const noticeKindChoices = (user: Parameters<typeof canOpenSidebarItem>[1]) => [
  "Information",
  ...(canOpenSidebarItem(getSidebarItem("calendar"), user) ? ["Event"] : []),
  "Poll",
  "Urgent",
];

/**
 * "Can you send a notice for me?": offer to prepare one, then ask which kind.
 * Sending still waits until they press Publish on the summary of that kind.
 */
const offerToSendEntry: HandoffEntry = {
  id: "create.offerToSend",
  sidebarItem: "create",
  examples: [
    "Can you send a notice for me?",
    "Could you send a notice for me?",
    "Would you send a notice for me?",
    "Will you send a notice for me?",
    "Send a notice for me",
    "Please send a notice for me",
    "Can you send out a notice for me?",
    "Could you send out a notice for me?",
    "I'd like you to send a notice for me",
    "Can you put a notice out for me?",
    "Would you put a notice out for me?",
    "Please can you send a notice for me?",
    "How do I create a notice?",
    "How do I create a notice for parents?",
    "How do I send a notice?",
    "how do i snd a notcie",
    "how do I send a notise",
    "How do I send a normal notice?",
    "Help me send a normal notice",
    "How can I send a normal notice?",
    "Can I also send a normal notice?",
    "how to create a notice",
    "I need to find out how to create a notice",
    "just send it dont ask me",
    "where can i make a new announcement",
    "post this without me checking",
    "i need to tell parents something",
    "Forget the event. How do I send a normal notice?",
    "help me write a notice",
    "send the notice now",
    "Can you offer to send one for me?",
  ],
  handoff: async (said, ctx) => {
    const notice = said.notice || "";
    if (/forget the event|normal notice/i.test(notice)) {
      return askFor(
        "noticeKind",
        "missing",
        "Choose an Information Notice on Create for a normal announcement. Add the title and message, choose who receives it, then review and press Publish. What should it say?",
        ["Information"]
      );
    }
    const polite = /^(?:can|could|would|will)\s+(?:you|we)\b/i.test(notice);
    return askFor(
      "noticeKind",
      "missing",
      polite
        ? OFFER_TO_SEND_TEXT
        : "I can prepare it on Create, but you still review it and press Publish. Nothing is sent until then. What kind of notice would you like to send?",
      noticeKindChoices(ctx.user)
    );
  },
};

/** "Use the Sports Day template": the wizard, started from that Template as Create notice does. */
const templateEntry: HandoffEntry = {
  id: "create.fromTemplate",
  sidebarItem: "create",
  followUpSlot: "template",
  examples: [
    "Start a notice from a template",
    "Use a template",
    "Use the template",
    "Start from a template",
    "Open a template",
    "Send a notice from a template",
    "Fill in a template",
    "Pick a template",
    "Start a notice with a template",
    "Use one of our templates",
    "Write a notice using a template",
    "use the birthday template",
  ],
  handoff: async (said, { user, token, queryClient }) => {
    const apiTemplates = await queryClient.fetchQuery(viewQuery(user, templatesQuery(user?.schoolId, token)));
    const templates = createPageTemplates(mapApiTemplates(apiTemplates), user?.schoolName);
    const names = templates.map((t) => t.name);
    if (!said.template) {
      return askFor("template", "missing", "Which Template would you like to use?", names);
    }
    const found = findByName(templates, said.template, (t) => t.name);
    if (found.length === 0) {
      return askFor(
        "template",
        "notFound",
        `I couldn't find a Template called “${said.template}”. Which one would you like?`,
        names
      );
    }
    if (found.length > 1) {
      return askFor("template", "ambiguous", "Which Template do you mean?", found.map((t) => t.name));
    }
    const [template] = found;
    return {
      kind: "ready",
      text: `I'll start a Notice from this Template for you to finish. ${NOTHING_SENT}`,
      summary: [{ label: "Template", value: template.name }],
      to: toCreateNotice({ template }),
    };
  },
};

const draftTitle = (draft: { title?: string }) => draft.title?.trim() || "Untitled Draft";

/** "Continue my draft about the trip": the wizard, reopened on that Draft as Create notice does. */
const draftEntry: HandoffEntry = {
  id: "create.continueDraft",
  sidebarItem: "create",
  followUpSlot: "draft",
  examples: [
    "Continue my last draft",
    "Continue my draft",
    "Finish my draft",
    "Open my draft",
    "Edit my draft",
    "Resume my draft",
    "Go back to my draft",
    "Carry on with my draft",
    "Keep working on my draft",
    "Finish the notice I started",
    "Continue the notice I was writing",
    "Pick up where I left off",
    "I have no drafts",
    ...askedHow("continue a draft"),
    "How do I reopen a draft?",
  ],
  handoff: async (said, { user, token, queryClient }) => {
    const notices = await queryClient.fetchQuery(viewQuery(user, noticesQuery(user?.schoolId, token)));
    // The Drafts Create notice lists, newest first
    const drafts = notices
      .filter((n) => n.isDraft && !n.isDeleted && n.noticeId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    if (drafts.length === 0) {
      return {
        kind: "cannot",
        text: "You don't have any Drafts right now. A draft is a Notice saved with Save Draft; it isn't sent until you press Publish.",
      };
    }
    const titles = drafts.map(draftTitle);
    const wanted = said.draft ?? said.topic;

    let found = drafts;
    if (wanted === LATEST) {
      found = drafts.slice(0, 1);
    } else if (wanted) {
      found = findByName(drafts, wanted, draftTitle);
      if (found.length === 0) {
        return askFor(
          "draft",
          "notFound",
          `I couldn't find a Draft called “${wanted}”. Which one would you like to continue?`,
          titles
        );
      }
    }
    if (found.length > 1) {
      return askFor(
        "draft",
        wanted ? "ambiguous" : "missing",
        "Which Draft would you like to continue?",
        found.map(draftTitle)
      );
    }
    const [draft] = found;
    return {
      kind: "ready",
      text: `I'll reopen this Draft for you to finish. ${NOTHING_SENT}`,
      summary: [{ label: "Draft", value: draftTitle(draft) }],
      to: toCreateNotice({ draft: draft as NoticeRestoreSource }),
    };
  },
};

/** "How do I send a worksheet or file?": both right answers, each with a button that starts it. */
const sendFileHowToEntry: HowToEntry = {
  id: "create.sendFileHowTo",
  sidebarItem: "create",
  examples: [
    "How do I send a worksheet or file?",
    "How do I send a file to parents?",
    "How do I attach a file to a notice?",
    "add a document to a notice",
    "How do I share a worksheet with a circle?",
    "Can I attach a document to a notice?",
    "How do I send parents a PDF?",
    "How do I share a file with a class?",
    "How do I send a worksheet to a circle?",
    "How do I attach a photo to a notice?",
    "How do I get a file to parents?",
    "i want to send a pdf",
    "stick a file on the announcement",
    "how do attachments work",
    "And attach a PDF.",
    ...askedHow("attach a PDF permission slip"),
    ...askedHow("attach the permission slip"),
    ...askedHow("send the file with a notice"),
  ],
  howTo: {
    load: async (ctx, asked) => {
      const canOpenDrive = canOpenSidebarItem(getSidebarItem("drive"), ctx.user);
      const canOpenLibrary = canOpenSidebarItem(getSidebarItem("library"), ctx.user);
      const aboutWorksheet = /\bworksheets?\b/i.test(asked);
      const drivePart = canOpenDrive ? " Or upload it to Drive and share it with a Circle." : "";
      const libraryPart =
        aboutWorksheet && canOpenLibrary
          ? " If it's one of ours, you'll find it ready-made in the Library."
          : "";
      return {
        text:
          /\bpdf\b/i.test(asked) && /^and\b/i.test(asked)
            ? "Attach the PDF on the Notice's writing step. That keeps it with this Notice."
            : // Where it's done, in the screen's own labels, so "where do I do that?" is already answered
              `On Create, start a Notice, then add the file under Attachment on its writing step.${drivePart}${libraryPart}`,
        actions: [
          { kind: "open", label: "Start a Notice", to: toNewNotice() },
          ...(canOpenDrive ? [{ kind: "open" as const, label: "Upload to Drive", to: toDriveUpload() }] : []),
        ],
      };
    },
  },
};

/** "How do I DM a specific parent?": a Notice can go to one parent, with a button that starts it. */
const dmUserHowToEntry: HowToEntry = {
  id: "create.dmUserHowTo",
  sidebarItem: "create",
  examples: [
    "How do I DM a specific parent?",
    "How do I DM a parent?",
    "How do I direct message a parent?",
    "How do I private message a parent?",
    "How do I message one parent?",
    "How do I message a specific parent?",
    "Can I send a notice to just one parent?",
    "How do I send a notice to one person?",
    "How do I message a parent directly?",
    "How do I send a private notice to one family?",
    "Not everyone. Only one parent.",
    "Can I send a notice to just one parent, Mrs Smith?",
    "Can I send a message only to Mr Naidoo?",
  ],
  howTo: {
    load: async () => ({
      text: "A Notice can go to one parent: on Review, under \"Who should receive this?\", pick the User instead of a Circle.",
      actions: [{ kind: "start", label: "Send to one parent", ask: "Send a notice to one parent" }],
    }),
  },
};

/** "How do I send an urgent notice?": Mark as urgent on the first step, from the course. */
const urgentHowToEntry: HowToEntry = {
  id: "create.urgentHowTo",
  sidebarItem: "create",
  examples: [
    "How do I send an urgent notice?",
    "Explain urgent notices",
    "Explain urgency",
    "Can you explain urgent notices?",
    "When should I use urgent?",
    "How do I mark a notice urgent?",
    "How do I make a notice urgent?",
    "Where's the urgent button?",
    "How do I send an emergency announcement?",
    "How do I turn on urgent?",
    "How do I flag a notice as urgent?",
    "How do I send something urgent?",
    "How do I mark a notice as urgent?",
    "How do I send an urgent announcement?",
    "need to tell parents something urgent",
    "Make that one urgent.",
    "Actually make that one urgent",
    "emergency announcement",
    ...askedHow("make that notice urgent"),
    ...askedHow("make the poll urgent"),
    ...askedHow("send an urgent notice"),
    "Just the urgent part",
    "Where is the urgent switch?",
    "don't make it urgent this time",
    "How do parents see urgent notices?",
    "How are parents told about an urgent notice?",
    "What happens when I send an urgent notice?",
  ],
  howTo: {
    load: async () => ({
      text: "On the first step of a Notice, turn on Mark as urgent notice. Parents are notified by email and push notification, so keep it for things that can't wait. Nothing is sent until you press Publish. I can do it for you if you want, or you can open Create.",
      actions: [
        { kind: "start", label: "Do it for me", ask: "Send an urgent notice" },
        { kind: "open", label: "Open Create", to: toNewNotice() },
      ],
    }),
  },
};

/** "How do I choose who receives a notice?": Circle or Everyone on Review. */
const audienceHowToEntry: HowToEntry = {
  id: "create.audienceHowTo",
  sidebarItem: "create",
  examples: [
    "How do I choose who receives a notice?",
    "How do I pick the audience?",
    "How do I choose who gets a notice?",
    "How do I send a notice to a circle?",
    "How do I pick a Circle or Everyone?",
    "Who should receive this?",
    "How do I pick who a notice goes to?",
    "How do I choose the recipients?",
    "How do I send it to one class?",
    "How do I stop the whole organization seeing a notice?",
    "only grade 2 parents",
    "send it to one class",
    "Only some parents should get it.",
    "I dont want the whole school to see it",
    ...askedHow("choose who receives it"),
    ...askedHow("choose who votes"),
    ...askedHow("send it only to that circle"),
    "And only Grade 2, not everyone",
    "only Grade 2, not everyone",
    "Only the same class as before",
  ],
  howTo: {
    load: async () => ({
      text: 'On Review, under "Who should receive this?", choose the Circle for those parents. Don\'t choose Everyone, because that is your whole Organization.',
      actions: [{ kind: "open", label: "Open Create", to: toNewNotice() }],
    }),
  },
};

const draftHowToEntry: HowToEntry = {
  id: "create.draftHowTo",
  sidebarItem: "create",
  examples: [
    "How do I save a draft?",
    "How do I save this and finish later?",
    "Where are my drafts?",
    "How do I carry on with a draft?",
    "How do I carry on with one?",
    "I don't want to send it yet",
    "How do I keep a notice as a draft?",
    "How do I save a notice without sending it?",
    "Where do drafts go?",
    "How do I come back to a notice later?",
    "Can I save a notice as a draft?",
    "How do I leave a notice unsent?",
    "Maybe it is still a draft",
  ],
  howTo: {
    load: async (_ctx, asked = "") => ({
      text: /where|drafts/i.test(asked) && !/save|finish later|don'?t want to send/i.test(asked)
        ? "Your Drafts are under Pick up where you left off, at the top of Create and Notices. Nothing is sent."
        : "Close the wizard with the cross before you publish, and press Save Draft. Your Drafts are under Pick up where you left off, at the top of Create and Notices. Nothing is sent.",
      actions: [{ kind: "open", label: "Open Create", to: toNewNotice() }],
    }),
  },
};

const claimListHowToEntry: HowToEntry = {
  id: "create.claimListHowTo",
  sidebarItem: "create",
  examples: [
    "How do I make a claim list?",
    "How do I create a claim list?",
    "How do I let parents claim a slot?",
    "Where is create a claim list?",
    "How do I make a sign up sheet for parents?",
    "sign up sheet for parents",
    "How do I send a claims list?",
    "How do claims work?",
    "What is a claim list?",
    "How do I let parents sign up for items?",
    "How do I make a list parents can claim from?",
    "i need a claims list",
    "I meant a claim list",
    "What was the claim list button called?",
    "Claims are on Create, not Calendar",
  ],
  howTo: {
    load: async () => ({
      text: "On Create, press Create a claim list on the Claims card. Parents claim one item each, and an option disappears once it's taken. Nothing is sent until you press Publish.",
      actions: [{ kind: "open", label: "Open Create", to: toNewNotice() }],
    }),
  },
};

const claimTakenHowToEntry: HowToEntry = {
  id: "create.claimTakenHowTo",
  sidebarItem: "create",
  examples: [
    "What happens when a parent claims an item?",
    "Can two parents claim the same thing?",
    "Do claimed slots vanish?",
    "Is a claim the same as a poll?",
    "What happens after someone claims an item?",
    "Does a claimed item disappear?",
    "Can more than one parent take the same item?",
    "What does claiming an item do?",
    "How does a claim list update?",
    "What do parents see after they claim something?",
  ],
  howTo: {
    load: async () => ({
      text: "Each family claims one item. Once something is taken it disappears for everyone else. Open the Notice from Notices: under Poll Results, each taken item says Claimed by and the person's name.",
      actions: [{ kind: "open", label: "Open Notices", to: { pathname: "/view-notices", state: undefined } }],
    }),
  },
};

const pollVoteHowToEntry: HowToEntry = {
  id: "create.pollVoteHowTo",
  sidebarItem: "create",
  examples: [
    "How do parents vote in a poll?",
    "Where do parents tap their vote?",
    "Can parents vote on the website?",
    "How does voting work?",
    "Do parents vote in the app?",
    "Where do families vote?",
    "How do parents answer a poll?",
    "Do parents vote on the dashboard?",
    "Where does a parent's vote happen?",
    "How do moms vote in a poll?",
    "Do I vote as well?",
    "Where do parents vote?",
  ],
  howTo: {
    load: async () => ({
      text: "Parents vote in the Noticeboard app, with one tap. You see the results on that Poll from Notices. You don't vote as the parent.",
      actions: [{ kind: "open", label: "Open Notices", to: { pathname: "/view-notices", state: undefined } }],
    }),
  },
};

const eventRepeatHowToEntry: HowToEntry = {
  id: "create.eventRepeatHowTo",
  sidebarItem: "create",
  examples: [
    "Can an event repeat every Friday?",
    "Can an event repeat?",
    "How do I make an event weekly?",
    "How do I make a repeating event?",
    "Can an event happen every week?",
    "How do I set an event to recur?",
    "Can I repeat an event every Friday?",
    "How do I make an event happen every week?",
    "Does an event repeat?",
    "How do I turn on repeat for an event?",
    "make it weekly",
    "can it happen every week",
    "recur every friday",
    "Same as before, every Friday.",
    "Can the poll repeat every term?",
    "It should happen every second week",
  ],
  howTo: {
    load: async (_ctx, asked = "") => ({
      text: /poll/i.test(asked)
        ? "Repeat is an Event setting, not a Poll setting. A Poll does not repeat every term by itself."
        : /friday/i.test(asked)
        ? "Set the Event's start date to a Friday, then choose Weekly under Repeat on Settings. The end date has to be at least one week after the start date. Nothing repeats until you set that and press Publish."
        : "On an Event's Settings step, Repeat makes it happen Daily, Weekly, Every 2 weeks or Monthly, until an End date. A Weekly Event must end at least one week after its Start date. Nothing repeats until you set that and press Publish.",
      actions: [{ kind: "open", label: "Open Create", to: toNewNotice() }],
    }),
  },
};

const eventReminderHowToEntry: HowToEntry = {
  id: "create.eventReminderHowTo",
  sidebarItem: "create",
  examples: [
    "Can I remind parents about an event?",
    "How do I remind parents about an event?",
    "How do event reminders work?",
    "Will parents get a nudge before an event?",
    "will they get a nudge",
    "event reminder",
    "How do I ping parents the day before an event?",
    "Can an event send a reminder?",
    "How do I set a reminder on an event?",
    "Do events remind parents?",
    "How do I remind them about Friday's event?",
    "Where is the event reminder setting?",
    "ping parents the day before",
    "What about reminding parents?",
    "Will parents get a reminder the day before?",
    "schedule a reminder",
    "Remind them the day before",
  ],
  howTo: {
    load: async (_ctx, asked = "") => ({
      text: /day before/i.test(asked)
        ? "Open the Event's Settings step and use the Reminder card to set a reminder for the day before. Review that setting before you publish. Nothing goes out until then."
        : "On an Event's Settings step, the Reminder card chooses when the app notifies people before the Event. Send reminder on Notices is for a Notice already sent. Nothing goes out until you publish it yourself.",
      actions: [{ kind: "open", label: "Open Create", to: toNewNotice() }],
    }),
  },
};

const eventAudienceHowToEntry: HowToEntry = {
  id: "create.eventAudienceHowTo",
  sidebarItem: "create",
  examples: [
    "How do I let only some parents see an event?",
    "How do I send an event to one circle?",
    "Who should receive this event?",
    "How do I stop the whole organization seeing an event?",
    "How do I choose who sees an event?",
    "Can an event go to one group of parents?",
    "How do I limit who sees an event?",
    "How do I send an event to some parents only?",
    "How do I pick the audience for an event?",
    "Can I send an event to one class?",
    "only grade 3 parents",
    "not the whole school",
    "Actually, I only want Grade 3 parents to see it.",
    "Only Grade 1 parents should see it",
    "What if there is no Grade 1 circle yet?",
  ],
  howTo: {
    load: async () => ({
      text: 'On Review, under "Who should receive this?", choose that Circle. Don\'t choose Everyone, because that is the whole Organization. Internal team only is for staff. If there is no Circle yet, an Owner or Admin has to create one. A Creator can\'t open Circles.',
      actions: [{ kind: "open", label: "Open Create", to: toNewNotice() }],
    }),
  },
};

const cancelEventHowToEntry: HowToEntry = {
  id: "create.cancelEventHowTo",
  sidebarItem: "create",
  examples: [
    "How do I cancel an event?",
    "How do I call off an event?",
    "The event is cancelled, what do I do?",
    "How do I remove an event from the calendar?",
    "How do parents know an event is cancelled?",
    "How do I tell parents an event is off?",
    "Can I cancel an event that's already sent?",
    "How do I take an event off the calendar?",
    "What do I do if an event is called off?",
    "How do I cancel a scheduled event?",
    "call off fridays event",
    "Can I change the date after I publish?",
    "What happens to the existing RSVPs if the date changes?",
    "Can I switch this poll to a claim list after publishing?",
    ...askedHow("cancel the scheduled send"),
    "change it to Saturday",
    "Can I change Friday to Monday after parents RSVPed?",
    "There is a cancelled or weather template",
  ],
  howTo: {
    load: async () => ({
      text: "A sent Event can't be edited. On Notices, press Duplicate and send a follow-up that tells parents, or start from the Cancelled / weather Template on Create. A Scheduled Event can be removed with Cancel schedule under Scheduled. Nothing is removed until you do that.",
      actions: [{ kind: "open", label: "Open Notices", to: { pathname: "/view-notices", state: undefined } }],
    }),
  },
};

const eventNotifyHowToEntry: HowToEntry = {
  id: "create.eventNotifyHowTo",
  sidebarItem: "create",
  examples: [
    "Will parents be notified when I create an event?",
    "Will parents get notified when I create an event?",
    "Will parents be told?",
    "Will the parents be told?",
    "Do parents get a notification for an event?",
    "Will parents be told about a new event?",
    "Does sending an event notify parents?",
    "Do parents get a push for an event?",
    "Are parents notified about events?",
    "Will families hear about an event I create?",
    "Does creating an event notify parents?",
    "When are parents told about an event?",
    "Do parents get notified before I publish an event?",
    "will moms be told",
    "Will parents be notified?",
    "does sending an event notify them",
    "notification for events",
    "Will parents be told if I cancel it?",
    "If I cancel the schedule, do parents get a cancellation message?",
    "Who will be notified?",
    "What do I send parents then?",
  ],
  howTo: {
    load: async () => ({
      text: "Parents are notified in the Noticeboard app when the Event is sent to them. Publishing a cancellation Notice notifies those parents too. Cancelling a scheduled send stops it going out, and does not send a cancellation message. A Draft, or stopping before Publish, doesn't notify them.",
      actions: [{ kind: "open", label: "Open Create", to: toNewNotice() }],
    }),
  },
};

const rsvpHowToEntry: HowToEntry = {
  id: "create.rsvpHowTo",
  sidebarItem: "create",
  examples: [
    "Can parents RSVP to an event?",
    "How do I ask who's coming to an event?",
    "How do RSVPs work?",
    "Can parents say if they're coming?",
    "Where do parents RSVP?",
    "How do I turn on RSVP?",
    "Do events have attendance replies?",
    "How do I see who's coming to an event?",
    "Can families reply to an event?",
    "how do i ask who's coming",
    "attendance replies for an event",
    "who said theyre coming",
    "rsvp",
    "Where do parents tap yes?",
    "Can parents RSVP?",
    "Is there a separate RSVP page?",
    ...askedHow("set a reply deadline"),
    ...askedHow("see who has responded"),
    "RSVP yes or no",
    "rsvp pls",
    "What happens to RSVPs?",
    "Parents must RSVP",
  ],
  howTo: {
    load: async () => ({
      text: "Turn on RSVPs on the Event's Details step if you need to know who's coming. Parents answer in the app. Open the Event from Notices to see Who responded. There isn't a separate RSVP page.",
      actions: [{ kind: "open", label: "Open Create", to: toNewNotice() }],
    }),
  },
};

const staffEventHowToEntry: HowToEntry = {
  id: "create.staffEventHowTo",
  sidebarItem: "create",
  examples: [
    "How do I make an event only for staff?",
    "How do I make an event for staff only?",
    "How do I make a staff only event?",
    "How do I make an event just for staff, not parents?",
    "Can I put something on the calendar without sending it to parents?",
    "Can I add an event to the calendar without a notice?",
    "How do I make a staff meeting on the calendar?",
    "How do I make an event just for the team?",
    "How do I hide an event from parents?",
    "What is internal team only?",
    "How do I send an event to staff only?",
    "Can an event be for the team only?",
    "How do I keep an event off the parent app?",
    "How do I make a staff-only event?",
    "Don't show this event to parents",
    "staff meeting on the calendar",
    "just for the team",
    "What if I only want the coaches to see it?",
    "Make the next one staff only.",
    "staff only event",
    "Staff only",
  ],
  howTo: {
    load: async () => ({
      text: 'On the Event\'s Review step, choose Internal team only under "Who should receive this?". Only your Team sees it. Parents do not.',
      actions: [{ kind: "open", label: "Open Create", to: toNewNotice() }],
    }),
  },
};

/** "How do I create a poll?": short steps, with a button that starts the guided Poll. */
const pollHowToEntry: HowToEntry = {
  id: "create.pollHowTo",
  sidebarItem: "create",
  examples: [
    "How do I create a poll?",
    "How do I make a poll?",
    "Make a poll",
    "How do I ask parents a question with choices?",
    "How do I send a poll to parents?",
    "How do I set up a poll?",
    "How do I run a poll?",
    "Can I create a poll for parents to vote on?",
    "How do I let parents vote on something?",
    "How do I send parents a quick vote?",
    "How do I create a poll with choices?",
    "i need parents to vote",
    "Now I need a poll.",
    "ask the class a question with options",
    "What's a Poll?",
    "What is a Poll?",
    "How do polls work?",
    "How does a poll work?",
    "whats a Poll",
    "I don't understand a Poll",
    "Tell me about a Poll",
    "Can you explain a Poll?",
    "What does a Poll mean here?",
    "Explain a Poll please",
  ],
  howTo: {
    load: async () => ({
      text: "Create a Poll with a question and choices; it goes to a Circle or Everyone, and each answer arrives with one tap.",
      actions: [{ kind: "start", label: "Create a poll", ask: "Create a poll" }],
    }),
  },
};

const imageNoticeHowToEntry: HowToEntry = {
  id: "create.imageNoticeHowTo",
  sidebarItem: "create",
  examples: [
    "How do I create an image notice?",
    "How do I send a picture notice?",
    "How do I send a photo as a notice?",
    "What is an image notice?",
    "How do I post a picture?",
    "How do I share a notice with an image?",
    "Where is the Image card?",
    "How do I make a photo notice?",
    "Can I send an image as a notice?",
    "How do I create a notice with a picture?",
    "I want it to be a picture.",
    "post a picture",
    ...askedHow("add a photo of the gate"),
    ...askedHow("add an image"),
  ],
  howTo: {
    load: async () => ({
      text: "On Create, choose Image: a Notice with a title and an image. Nothing is sent until you press Publish.",
      actions: [{ kind: "open", label: "Open Create", to: toNewImageNotice() }],
    }),
  },
};

/** Polls aren't anonymous: Poll Results lists who voted and what each person chose. */
const pollPrivacyHowToEntry: HowToEntry = {
  id: "create.pollPrivacy",
  sidebarItem: "create",
  examples: [
    "Can polls be anonymous?",
    "Will I see who voted?",
    "Are polls anonymous?",
    "Can staff see who voted?",
    "Can I make an anonymous poll?",
    "Can I make a poll anonymous?",
    "Can other parents see what I voted?",
    "Do parents' votes stay private?",
    "Can staff see which option a parent chose?",
    "Is voting in a poll anonymous?",
    "Will people know how I voted?",
    "Can I hide who voted on a poll?",
    "Can a poll be private?",
    "Can you see who voted for what?",
    "Are poll answers confidential?",
    "When a parent votes on a poll, can staff see who voted and which option they chose?",
    "Can staff see who voted and what they chose?",
  ],
  howTo: {
    load: async () => ({
      text: "No, Polls aren't anonymous. On Poll Results, Who Voted lists each person's name and the choice they picked, and anyone who can open the Poll in Notices sees it. For answers nobody should be able to trace, don't use a Poll.",
      actions: [],
    }),
  },
};

/** Bold, italic and lists: on the writing step when formatting is switched on for the Organization. */
const formatTextHowToEntry: HowToEntry = {
  id: "create.formatTextHowTo",
  sidebarItem: "create",
  examples: [
    "Can I make text bold or italic in a notice?",
    "How do I format the message text?",
    "How do I add bullet points to a notice?",
    "Can I use bold in a notice?",
    "How do I make words italic?",
    "Can I add a numbered list?",
    "How do I underline text in a notice?",
    "Is there formatting for the message?",
    "How do I make a heading bold?",
    "Can I format my notice?",
    "Where is the bold button?",
  ],
  howTo: {
    load: async (ctx) =>
      isFeatureFlagEnabled(getFeatureFlagsFromUser(ctx.user), "noticeBodyFormatting")
        ? {
            text: "Yes. On the writing step, select the words and use the buttons above the message: Bold, Italic, Underline, Bullet list or Numbered list.",
            actions: [{ kind: "open", label: "Open Create", to: toNewNotice() }],
          }
        : {
            text: "Not yet for your Organization: the message is plain text, without bold, italics or lists. A new line in the message stays a new line.",
            actions: [],
          },
  },
};

/** What an image notice takes: any image file, up to 25MB. */
const imageFilesHowToEntry: HowToEntry = {
  id: "create.imageFilesHowTo",
  sidebarItem: "create",
  examples: [
    "What image formats and sizes can I upload for an image notice?",
    "What size can the picture be?",
    "Can I upload a PNG?",
    "Can I use a JPG for an image notice?",
    "How big can the image be?",
    "What's the maximum image size?",
    "Which picture formats work?",
    "Is there a size limit for photos?",
    "Can I upload a GIF?",
    "What file types can an image notice use?",
  ],
  howTo: {
    load: async () => ({
      text: "An image notice takes any image file (JPG, PNG, WebP or GIF, for example) up to 25MB. A message under the picture is optional.",
      actions: [{ kind: "open", label: "Open Create", to: toNewImageNotice() }],
    }),
  },
};

const everyoneHowToEntry: HowToEntry = {
  id: "create.everyoneHowTo",
  sidebarItem: "create",
  examples: [
    "How do I send a notice to everyone?",
    "Can I send a notice to everyone?",
    "How do I send a notice to the whole school?",
    "How do I send it to all parents?",
    "How do I send a notice to everyone in the organization?",
    "How do I choose Everyone?",
    "How do I send a notice to all users?",
    "Where do I pick Everyone?",
    "How do I send to the whole organization?",
    "Can a notice go to everyone?",
    "How do I send a notice to all families?",
    "Everyone needs to see it",
    "the whole school",
    "all parents",
    "everyone in the organization",
    "Send the next notice to everyone.",
    "send it to all users",
    "A different event for all members",
    "Everyone by mistake is bad, right?",
  ],
  howTo: {
    load: async ({ user }) => ({
      text: user?.accessLevel?.toLowerCase() !== "admin"
        ? "Only an Admin can send to Everyone. Choose one of your assigned Circles on Review."
        : 'On Review, under "Who should receive this?", choose Everyone. That is your whole Organization, not one parent.',
      actions: [{ kind: "open", label: "Open Create", to: toNewNotice() }],
    }),
  },
};

const useTemplateHowToEntry: HowToEntry = {
  id: "create.useTemplate",
  sidebarItem: "create",
  examples: [
    "How do I use a template?",
    "How do I use a saved notice template?",
    "How do I use a saved template?",
    "How do I use templates?",
    "How do templates work?",
    "Where is Explore Templates?",
    // A teacher asking for "the templates" means Notice templates; certificate templates are asked for by name
    "Where are the templates?",
    "Where can I find templates?",
    "How do I browse templates?",
    "What are the template filters?",
    "How do I pick a template on Create?",
    "What happens when I choose a template?",
    "Where are the System templates?",
    "How do I use a ready-made template?",
    "Show me how templates work",
    "How do I start a notice from Explore Templates?",
    "What kinds of templates are there?",
    ...askedHow("find a notice template instead"),
    ...askedHow("find a template"),
    "the template section?",
    "Templates are there too",
  ],
  howTo: {
    load: async () => ({
      text: "On Create, scroll to Explore Templates. Filters are All, System, Information and Event. Choosing one starts a notice you still review. Nothing is sent until you press Publish.",
      actions: [{ kind: "open", label: "Open Create", to: { pathname: "/create-notice", state: undefined } }],
    }),
  },
};

const createPageHowToEntry: HowToEntry = {
  id: "create.pageHowTo",
  sidebarItem: "create",
  examples: [
    "What can I create from the Create page?",
    "What are the create options?",
    "What's on the Create page?",
    "Which cards are on Create?",
    "What can I make on Create?",
    "What does the Create page include?",
    "List what I can create",
    "What notice types can I start?",
    "What's on Create besides a notice?",
    "Show me the Create cards",
    "which notice types are there",
    "Can I copy last year's poll?",
    "Where are signatures?",
    ...askedHow("open create"),
  ],
  howTo: {
    load: async (_ctx, asked = "") => ({
      text: /copy|duplicate|last year/i.test(asked)
        ? "Duplicate from Sent copies a Notice into Create. Nothing is sent until you press Publish."
        : "Create has Create Notice, Image, Events, Poll, Claims, Attachments, Certificates and Signatures, plus Explore Templates.",
      actions: [{ kind: "open", label: "Open Create", to: toNewNotice() }],
    }),
  },
};

const pollChoicesHowToEntry: HowToEntry = {
  id: "create.pollChoicesHowTo",
  sidebarItem: "create",
  examples: [
    "How many choices can a poll have?",
    "What is the poll option limit?",
    "Can I have 12 options on a poll?",
    "How many answers can a poll have?",
    "Is there a limit on poll choices?",
    "How many options can I add to a poll?",
    "Can a poll have more than 10 choices?",
    "What's the maximum number of poll choices?",
    "How many choices are allowed?",
    "Poll choice limit",
    "How many choices can it have?",
  ],
  howTo: {
    load: async () => ({
      text: `A Poll asks one question with up to ${POLL_MAX_OPTIONS} choices.`,
      actions: [{ kind: "open", label: "Open Create", to: toNewNotice() }],
    }),
  },
};

const claimVsPollHowToEntry: HowToEntry = {
  id: "create.claimVsPoll",
  sidebarItem: "create",
  examples: [
    "Is a claim list the same as a poll?",
    "Claim vs poll",
    "Should I use a poll or a sign up sheet?",
    "What's the difference between a claim list and a poll?",
    "Is a claim a poll?",
    "Poll or claim list?",
    "How is a claim list different from a poll?",
    "Do claims and polls work the same?",
    "When do I use a claim list instead of a poll?",
    "Are claims and polls the same?",
    "Is that the same as a claim list?",
    "thats the same as rsvp right",
    "How is that different from a poll?",
  ],
  howTo: {
    load: async () => ({
      text: "Use a Poll when people vote on a question. Use a claim list when each family takes one item, and that option disappears.",
      actions: [{ kind: "open", label: "Open Create", to: toNewNotice() }],
    }),
  },
};

/** "I made it as a notice I think": why an Event isn't on the Calendar, and what to do instead. */
const madeAsNoticeHowToEntry: HowToEntry = {
  id: "create.madeAsNotice",
  sidebarItem: "create",
  examples: [
    "I made it as a notice I think",
    "I sent it as a notice, not an event",
    "I made a notice instead of an event",
    "It was a notice not an event",
    "I think I sent it as an information notice",
    "I sent it as an Information notice",
    "Oops, I made a notice not an event",
    "It went out as a notice",
    "I chose Information instead of Event",
    "Can I turn my notice into an event?",
    "My notice isn't on the calendar",
  ],
  howTo: {
    load: async () => ({
      text: "That's why it isn't there: only Events show on the Calendar, and a sent Notice can't be turned into one. Make it again as an Event, with its date and time, for the same Circle.",
      actions: [{ kind: "start", label: "Make the Event", ask: "create an event" }],
    }),
  },
};

/** "Can I set a closing date on a poll?": only from the chat for now; the Create screen has no field for it. */
const pollClosingHowToEntry: HowToEntry = {
  id: "create.pollClosing",
  sidebarItem: "create",
  examples: [
    "Can I set a closing date on a poll?",
    "Can a poll close on a date?",
    "How do I close a poll?",
    "How do I stop people voting?",
    "How do I stop people voting then?",
    "Can I set a deadline for a poll?",
    "When does a poll close?",
    "Can I end a poll?",
    "Can voting close automatically?",
    "Can a poll have an end date?",
  ],
  howTo: {
    load: async () => ({
      text: "Yes, when you make the Poll with me: say when it closes, like “closes Friday at 5pm”, and voting stops then. The Create screen doesn't have a closing date of its own yet, and a Poll already sent can't be changed.",
      actions: [{ kind: "start", label: "Make a Poll", ask: "create a poll" }],
    }),
  },
};

/** "Can parents choose more than one option?": one answer each, kept once given. */
const pollOneAnswerHowToEntry: HowToEntry = {
  id: "create.pollOneAnswer",
  sidebarItem: "create",
  examples: [
    "Can parents choose more than one option in a poll?",
    "Can parents pick two choices?",
    "Can I make a multiple choice poll?",
    "Can parents change their vote?",
    "Can a parent vote twice?",
    "Can people select more than one answer?",
    "Can parents tick more than one box?",
    "Can a poll allow several answers?",
    "Can parents vote for two options?",
    "Can I let parents choose many options?",
  ],
  howTo: {
    load: async () => ({
      text: "No. Each parent picks one choice, once, and can't change it after voting. To ask about several things, send a Poll for each.",
      actions: [{ kind: "open", label: "Open Create", to: toNewNotice() }],
    }),
  },
};

/** "Can a parent unclaim something?": a claim is final in the app. */
const claimFinalHowToEntry: HowToEntry = {
  id: "create.claimFinal",
  sidebarItem: "create",
  examples: [
    "Can a parent unclaim something?",
    "Can a parent unclaim something if they change their mind?",
    "Can a parent give back an item they claimed?",
    "Can a parent change what they claimed?",
    "What if a parent claims the wrong item?",
    "Can parents swap claims?",
    "Can a parent cancel their claim?",
    "Can someone undo a claim?",
    "What if a parent can't bring what they claimed?",
    "Can a parent release an item?",
  ],
  howTo: {
    load: async () => ({
      text: "No. Once a parent claims an item it's theirs, and the app doesn't let them change it or give it back. If plans change, ask them to let you know, and see who has what under Poll Results on that Notice.",
      actions: [{ kind: "open", label: "Open Notices", to: { pathname: "/view-notices", state: undefined } }],
    }),
  },
};

/** "Can I edit a claim list after I sent it?": like any sent Notice, it's duplicated, not edited. */
const claimEditHowToEntry: HowToEntry = {
  id: "create.claimEditAfterSend",
  sidebarItem: "create",
  examples: [
    "Can I edit a claim list after I sent it?",
    "Can I add items to a claim list I already sent?",
    "How do I change a claim list after sending?",
    "Can I change a poll after I sent it?",
    "Can I edit a claims list after publishing?",
    "How do I fix a claim list I already sent?",
    "Can I remove an item from a sent claim list?",
    "I forgot an item on my claim list",
    "Can I add more options to a sent poll?",
    "Can I update a claim list?",
  ],
  howTo: {
    load: async () => ({
      text: "No, a sent claim list or Poll can't be edited. On Notices, press Duplicate on it, change the items, and send the new one. Tell parents which one to use, since claims on the old one stay there.",
      actions: [{ kind: "open", label: "Open Notices", to: { pathname: "/view-notices", state: undefined } }],
    }),
  },
};

/** "How do I schedule a notice for later?": explain the last step, then start the Notice. */
const scheduleOfferEntry: HandoffEntry = {
  id: "create.scheduleOffer",
  sidebarItem: "create",
  examples: [
    "How do I schedule a notice for later?",
    "How do I schedule a notice for tomorrow?",
    "Help me schedule a notice",
    "I want to schedule a notice for later",
    "Can you schedule a notice for later?",
    "Schedule my notice for later",
    "I need to schedule a notice",
    "How can I schedule a notice for later?",
    "Please schedule a notice for later",
    "Walk me through scheduling a notice",
    "send this tomorrow morning",
    "Schedule it for tomorrow morning.",
    "post it on friday not now",
  ],
  handoff: async (said, ctx) =>
    askFor(
      "noticeKind",
      "missing",
      /tomorrow morning/i.test(said.notice || "")
        ? "I can assist with that. What time tomorrow morning? On the last step, choose Schedule for later, set tomorrow's date and that time, and press Schedule. What kind of notice would you like to send?"
        : "I can assist with that. On the last step, choose Schedule for later, pick the Publish date and Publish time, and press Schedule. What kind of notice would you like to send?",
      noticeKindChoices(ctx.user)
    ),
};

/** "How do I send a notice to one parent?": offer to do it, then ask who. */
const sendToOneParentEntry: HandoffEntry = {
  id: "create.sendToOneParent",
  sidebarItem: "create",
  examples: [
    "How do I send a notice to one parent?",
    "Can I send something only to one parent?",
    "Can I send a notice to just one parent?",
    "How do I send a notice to a single parent?",
    "Send a notice to one parent please",
    "I want to send a notice to one parent",
    "Can you send a notice to one parent?",
    "Help me send a notice to one parent",
    "How do I message one parent with a notice?",
    "Send this notice to one parent",
    "I need to send a notice to one parent",
    "How do I reach one parent with a notice?",
    "dm one parent",
    "private message a user",
    "Send one to a single parent.",
    "send something to just one mom",
    "direct notice",
    ...askedHow("send a notice to one parent"),
  ],
  handoff: async (said) => {
    if (!said.user) {
      return askFor(
        "user",
        "missing",
        "I can assist with that. A Notice can go to one parent. Who do you want to send it to?",
        []
      );
    }
    return {
      kind: "ready",
      text: `I'll open a Notice for ${said.user}. On Review, under "Who should receive this?", pick that parent. Nothing is sent until you press Publish.`,
      summary: [{ label: "To", value: said.user }],
      to: toNewNotice(),
    };
  },
};

export const createEntries = [
  openPageEntry("create", "It's where you start a new Notice or use a Template.", [
    "Where do I start a new notice?",
    "where are the notice templates",
    "Where is the Create page?",
    "where is the add notice button",
    "i dont want a blank notice",
    "I'll open Create and stop there",
  ]),
  noticeEntry,
  offerToSendEntry,
  scheduleOfferEntry,
  sendToOneParentEntry,
  templateEntry,
  draftEntry,
  sendFileHowToEntry,
  dmUserHowToEntry,
  urgentHowToEntry,
  audienceHowToEntry,
  draftHowToEntry,
  claimListHowToEntry,
  claimTakenHowToEntry,
  pollVoteHowToEntry,
  eventRepeatHowToEntry,
  eventReminderHowToEntry,
  eventAudienceHowToEntry,
  cancelEventHowToEntry,
  eventNotifyHowToEntry,
  rsvpHowToEntry,
  staffEventHowToEntry,
  imageNoticeHowToEntry,
  imageFilesHowToEntry,
  formatTextHowToEntry,
  pollPrivacyHowToEntry,
  everyoneHowToEntry,
  useTemplateHowToEntry,
  createPageHowToEntry,
  pollChoicesHowToEntry,
  claimVsPollHowToEntry,
  madeAsNoticeHowToEntry,
  pollClosingHowToEntry,
  pollOneAnswerHowToEntry,
  claimFinalHowToEntry,
  claimEditHowToEntry,
  pollHowToEntry,
];
